import { useAtomValue } from 'jotai'
import { memo, useMemo } from 'react'
import { LiveField } from '../../../rapid-ui/inputs/live-field'
import { canvasIsGeneratingAtom, externalRefAtoms } from '../atoms'
import { useCanvasStore } from '../canvas-store'
import type {
	Background,
	BorderRadiusCorners,
	Spacing,
	TextAlignMode,
	TextAlignValue,
	TextContentMode,
	TextHighlight,
	TextRefTarget,
	TextStyle,
} from '../data/v1'
import { borderRadiusStyle } from '../lib/border-radius'
import { textHighlightStyle } from '../lib/text-highlight'
import { useFontFamilyStyle } from '../lib/use-font-family-style'
import { useResolvedColor } from '../lib/use-resolved-color'
import { useDesignerActions } from '../use-designer-actions'
import { useExternalCanvasDataActions } from '../use-external-canvas-data-actions'
import { BackgroundFill } from './background-fill'

export type TextLayerRendererProps = {
	layerId: string
	content: TextContentMode
	refTo: TextRefTarget
	customValue: string
	textAlignMode: TextAlignMode
	textAlignValue: TextAlignValue
	spacing: Spacing
	background: Background
	textStyle: TextStyle
	textHighlight: TextHighlight
	borderRadius: number
	borderRadiusCorners: BorderRadiusCorners
}

const EditableRefText = memo(
	function EditableRefText({ refTo, style }: { refTo: TextRefTarget; style: React.CSSProperties }) {
		const store = useCanvasStore()
		const value = useAtomValue(externalRefAtoms[refTo], { store })
		const isGenerating = useAtomValue(canvasIsGeneratingAtom, { store })
		const { updateExternalField } = useExternalCanvasDataActions()

		return (
			<LiveField
				value={value || ''}
				onChange={(v) => updateExternalField(refTo, v)}
				placeholder={`{${refTo}}`}
				fitSize={false}
				showHoverRing={false}
				isGenerating={isGenerating}
				style={style}
			/>
		)
	},
)

const EditableCustomText = memo(
	function EditableCustomText(
		{ layerId, customValue, style }: { layerId: string; customValue: string; style: React.CSSProperties },
	) {
		const { updateLayer } = useDesignerActions()

		return (
			<LiveField
				value={customValue}
				onChange={(v) => updateLayer(layerId, { customValue: v })}
				placeholder="..."
				fitSize={false}
				showHoverRing={false}
				style={style}
			/>
		)
	},
)

export const TextLayerRenderer = memo(
	function TextLayerRenderer(
		{
			layerId,
			content,
			refTo,
			customValue,
			textAlignMode,
			textAlignValue,
			spacing,
			background,
			textStyle,
			textHighlight,
			borderRadius,
			borderRadiusCorners,
		}: TextLayerRendererProps,
	) {
		const { selectLayer } = useDesignerActions()
		const resolvedColor = useResolvedColor(textStyle.textColor)
		const resolvedHighlightColor = useResolvedColor(textHighlight.color)
		const highlight = useMemo(
			() => textHighlightStyle(textHighlight, resolvedHighlightColor),
			[textHighlight, resolvedHighlightColor],
		)
		const { style: fontFamilyStyle } = useFontFamilyStyle(
			textStyle.fontFamilyMode,
			textStyle.fontFamilyProvider,
			textStyle.fontFamilyGoogleFamily,
			textStyle.fontFamilyCustomUrl,
			textStyle.fontFamilyCustomName,
		)

		const cornerRadiusStyle = borderRadiusStyle(borderRadius, borderRadiusCorners)

		const style: React.CSSProperties = {
			...(textAlignMode !== 'auto' && { textAlign: textAlignValue }),
			...fontFamilyStyle,
			color: textStyle.textColorMode !== 'auto' ? resolvedColor : undefined,
			...(textStyle.fontSizeMode === 'value' && { fontSize: textStyle.fontSizePxValue }),
			...(textStyle.fontWeightMode === 'value' && { fontWeight: textStyle.fontWeightValue }),
			...(textStyle.fontStyleMode !== 'auto' && { fontStyle: textStyle.fontStyleValue }),
			...(textStyle.lineHeightMode === 'value' && { lineHeight: textStyle.lineHeightValue }),
			...(textStyle.textShadowMode === 'value' && textStyle.textShadowValue === 'none' && { textShadow: 'none' }),
			...(textStyle.textShadowMode === 'value' && textStyle.textShadowValue === 'simple' && {
				textShadow: `0 0 ${textStyle.textShadowSimple.spread}px rgba(0,0,0,${
					textStyle.textShadowSimple.opacity / 100
				})`,
			}),
			...cornerRadiusStyle,
			paddingTop: spacing.paddingTop,
			paddingBottom: spacing.paddingBottom,
			paddingLeft: spacing.paddingLeft,
			paddingRight: spacing.paddingRight,
			marginTop: spacing.marginTop,
			marginBottom: spacing.marginBottom,
			marginLeft: spacing.marginLeft,
			marginRight: spacing.marginRight,
		}

		return (
			<div
				data-layer-id={layerId}
				style={{ ...style, pointerEvents: 'auto' }}
				className="relative"
				onFocusCapture={() => selectLayer(layerId)}
				onClick={(e) => {
					const editable = e.currentTarget.querySelector<HTMLElement>('[contenteditable]')
					if (editable && document.activeElement !== editable) {
						editable.focus()
					}
				}}
			>
				<div className="absolute inset-0 overflow-hidden" style={cornerRadiusStyle}>
					<BackgroundFill background={background} />
				</div>
				<div className="relative" style={highlight.wrapper}>
					{content === 'ref'
						? <EditableRefText refTo={refTo} style={highlight.text} />
						: <EditableCustomText layerId={layerId} customValue={customValue} style={highlight.text} />}
				</div>
			</div>
		)
	},
)
